import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Buildings, House, Plus, DotsThree, Trash, PencilSimple, ArrowCounterClockwise, CaretDown, Check } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import ConfirmDelete from "@/components/ConfirmDelete";
import { DOC_VERSION } from "@/legal/legalDocs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = ["Commercio", "Industria", "Terziario", "Artigianato", "Edilizia", "Trasporti", "Sanita", "Tecnologia", "Consulenza", "Ristorazione", "Turismo", "Agricoltura", "Altro"];
const EMP_RANGES = ["1-10", "11-50", "51-150", "151-500", "500+"];

const EMPTY_WS = { name: "", company_data: { ragione_sociale: "", piva: "", sdi: "", indirizzo: "", settore: "", dipendenti_stimati: "" } };

export default function WorkspaceSwitcher() {
  const { user, activeWorkspaceId, setActiveWorkspace, refreshUser } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null);  // workspace da editare
  const [showTrash, setShowTrash] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/workspaces`, { withCredentials: true });
      setWorkspaces(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Ascolta click sul WorkspaceIndicator in header per fare "flash" + aprire switcher
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    const handler = () => {
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);
    };
    window.addEventListener("focus-workspace-switcher", handler);
    return () => window.removeEventListener("focus-workspace-switcher", handler);
  }, []);

  if (!user || user.account_type !== "agency") return null;

  const active = workspaces.find(w => w.id === activeWorkspaceId);
  const activeList = workspaces.filter(w => !w.deleted_at);
  const trashList = workspaces.filter(w => w.deleted_at);

  const onSwitch = async (wsId) => {
    if (wsId === activeWorkspaceId) return;
    try {
      await setActiveWorkspace(wsId);
      const ws = workspaces.find(w => w.id === wsId);
      toast.success(`Workspace attivo: ${ws?.name || ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore switch workspace");
    }
  };

  const onDelete = async (wsId) => {
    try {
      await axios.delete(`${API}/workspaces/${wsId}`, { withCredentials: true });
      toast.success("Workspace spostato nel cestino (15 giorni per ripristinare)");
      // Backend auto-switches active to self if deleted was active; sync frontend state
      if (wsId === activeWorkspaceId) await refreshUser();
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className={`px-3 pt-3 pb-2 border-b border-white/10 transition-all ${flash ? "ring-2 ring-[#56A3F5] ring-offset-2 ring-offset-[#0D1F3C] rounded-lg" : ""}`} data-testid="workspace-switcher">
      <p className="text-[10px] uppercase tracking-wider text-white/40 mb-1.5 px-2">Workspace</p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm transition-all"
            data-testid="ws-switcher-trigger"
          >
            {active?.type === "self" ? <House size={16} weight="duotone" /> : <Buildings size={16} weight="duotone" />}
            <span className="flex-1 text-left truncate font-medium">{active?.name || (loading ? "Caricamento..." : "—")}</span>
            <CaretDown size={12} weight="bold" className="opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="start" className="w-64" data-testid="ws-switcher-menu">
          <DropdownMenuLabel className="text-[11px] text-[#0D1F3C]/60">I tuoi workspace</DropdownMenuLabel>
          {activeList.map(w => (
            <div key={w.id} className="flex items-center group">
              <DropdownMenuItem
                onClick={() => onSwitch(w.id)}
                className="flex-1 gap-2 cursor-pointer"
                data-testid={`ws-item-${w.id}`}
              >
                {w.type === "self" ? <House size={14} weight="duotone" className="text-[#1B4FD8]" /> : <Buildings size={14} weight="duotone" className="text-[#0D1F3C]/60" />}
                <span className="flex-1 truncate">{w.name}</span>
                {w.id === activeWorkspaceId && <Check size={14} className="text-emerald-600" weight="bold" />}
              </DropdownMenuItem>
              {w.type !== "self" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      onClick={e => e.stopPropagation()}
                      className="p-1.5 mr-1 rounded opacity-0 group-hover:opacity-100 hover:bg-[#0D1F3C]/5 transition-opacity"
                      data-testid={`ws-actions-${w.id}`}
                    >
                      <DotsThree size={16} weight="bold" className="text-[#0D1F3C]/60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem onClick={() => setShowEdit(w)} className="gap-2" data-testid={`ws-edit-${w.id}`}>
                      <PencilSimple size={14} weight="duotone" /> Modifica
                    </DropdownMenuItem>
                    <ConfirmDelete
                      title={`Eliminare "${w.name}"?`}
                      description="I dati saranno conservati per 15 giorni nel cestino, poi eliminati definitivamente."
                      onConfirm={() => onDelete(w.id)}
                    >
                      <DropdownMenuItem onSelect={e => e.preventDefault()} className="gap-2 text-red-600 focus:text-red-700" data-testid={`ws-delete-${w.id}`}>
                        <Trash size={14} weight="duotone" /> Elimina
                      </DropdownMenuItem>
                    </ConfirmDelete>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}

          {trashList.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowTrash(true)} className="gap-2 text-[#0D1F3C]/70" data-testid="ws-trash-open">
                <Trash size={14} weight="duotone" />
                Cestino ({trashList.length} {trashList.length === 1 ? "ripristinabile" : "ripristinabili"})
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowCreate(true)} className="gap-2 text-[#1B4FD8] font-medium" data-testid="ws-add-client">
            <Plus size={14} weight="bold" /> Aggiungi Cliente
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateEditDialog
        open={showCreate} onClose={() => setShowCreate(false)}
        onSaved={async (newWs) => {
          await reload();
          if (newWs?.id) await setActiveWorkspace(newWs.id);
          toast.success("Workspace cliente creato");
        }}
      />
      <CreateEditDialog
        open={!!showEdit} workspace={showEdit}
        onClose={() => setShowEdit(null)}
        onSaved={async () => { await reload(); toast.success("Workspace aggiornato"); }}
      />
      <TrashDialog
        open={showTrash} onClose={() => setShowTrash(false)}
        workspaces={trashList} onChange={reload}
      />
    </div>
  );
}

function CreateEditDialog({ open, onClose, workspace, onSaved }) {
  const isEdit = !!workspace;
  const [form, setForm] = useState(EMPTY_WS);
  const [busy, setBusy] = useState(false);
  // B71-QUAT TASK 5: addendum agenzie obbligatorio al PRIMO workspace cliente
  const [addendumState, setAddendumState] = useState({ needs: false, loading: true });
  const [acceptAddendum, setAcceptAddendum] = useState(false);

  useEffect(() => {
    if (open) {
      if (isEdit) setForm({
        name: workspace.name || "",
        company_data: { ...EMPTY_WS.company_data, ...(workspace.company_data || {}) },
      });
      else setForm(EMPTY_WS);
      setAcceptAddendum(false);
      // Check status addendum solo in creazione (non in edit)
      if (!isEdit) {
        setAddendumState({ needs: false, loading: true });
        axios.get(`${API}/me/addendum-status`, { withCredentials: true })
          .then(r => setAddendumState({ needs: !r.data.accepted, loading: false }))
          .catch(() => setAddendumState({ needs: true, loading: false })); // failsafe: mostra spunta
      }
    }
  }, [open, workspace, isEdit]);

  const setCD = (k, v) => setForm(f => ({ ...f, company_data: { ...f.company_data, [k]: v } }));

  const save = async () => {
    const rs = (form.company_data.ragione_sociale || "").trim();
    const piva = (form.company_data.piva || "").trim();
    if (!form.name.trim()) return toast.error("Nome workspace obbligatorio");
    if (!rs) return toast.error("Ragione sociale obbligatoria");
    if (!piva) return toast.error("P.IVA obbligatoria");
    // B71-QUAT TASK 5: blocca se non spuntato l'addendum al primo workspace
    if (!isEdit && addendumState.needs && !acceptAddendum) {
      return toast.error("Devi accettare l'Addendum Partner Agenzie per creare un workspace cliente");
    }
    setBusy(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/workspaces/${workspace.id}`, form, { withCredentials: true });
        onSaved && onSaved();
      } else {
        const payload = { ...form };
        if (addendumState.needs) {
          payload.accept_addendum = true;
          payload.doc_version = DOC_VERSION;
        }
        const { data } = await axios.post(`${API}/workspaces`, payload, { withCredentials: true });
        onSaved && onSaved(data);
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ws-create-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0D1F3C]">{isEdit ? "Modifica Workspace" : "Aggiungi Cliente"}</DialogTitle>
          <DialogDescription className="text-xs text-[#0D1F3C]/60">
            {isEdit ? "Aggiorna i dati del workspace." : "Crea un nuovo workspace cliente separato dai tuoi dati interni."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs mb-1 block">Nome workspace *</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Es: Hot & Cold Srl" data-testid="ws-name" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Ragione sociale *</Label>
            <Input value={form.company_data.ragione_sociale || ""} onChange={e => setCD("ragione_sociale", e.target.value)} data-testid="ws-rs" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">P.IVA *</Label>
              <Input value={form.company_data.piva || ""} onChange={e => setCD("piva", e.target.value)} data-testid="ws-piva" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">SDI / PEC</Label>
              <Input value={form.company_data.sdi || ""} onChange={e => setCD("sdi", e.target.value)} data-testid="ws-sdi" />
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1 block">Indirizzo</Label>
            <Input value={form.company_data.indirizzo || ""} onChange={e => setCD("indirizzo", e.target.value)} data-testid="ws-addr" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs mb-1 block">Settore</Label>
              <Select value={form.company_data.settore || ""} onValueChange={v => setCD("settore", v)}>
                <SelectTrigger data-testid="ws-sector"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Dipendenti stimati</Label>
              <Select value={form.company_data.dipendenti_stimati || ""} onValueChange={v => setCD("dipendenti_stimati", v)}>
                <SelectTrigger data-testid="ws-emp"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{EMP_RANGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {/* B71-QUAT TASK 5: accettazione obbligatoria Addendum Partner Agenzie (solo primo workspace) */}
          {!isEdit && !addendumState.loading && addendumState.needs && (
            <div className="mt-2 pt-3 border-t border-[#0D1F3C]/10" data-testid="ws-addendum-block">
              <label className="flex items-start gap-2.5 cursor-pointer" htmlFor="ws-cb-addendum">
                <Checkbox
                  id="ws-cb-addendum"
                  checked={acceptAddendum}
                  onCheckedChange={(v) => setAcceptAddendum(Boolean(v))}
                  data-testid="ws-accept-addendum"
                  className="mt-0.5"
                />
                <span className="text-xs text-[#0D1F3C]/80 leading-relaxed">
                  Dichiaro di operare quale Agenzia per conto di clienti terzi e accetto l'
                  <Link to="/legal/addendum-agenzie" target="_blank" rel="noopener" className="text-[#1B4FD8] underline" data-testid="ws-link-addendum">
                    Addendum Partner Agenzie
                  </Link>
                  , incluse, ex artt. 1341-1342 c.c., le clausole:
                  {" "}<strong>Art. 4</strong> (divieti rafforzati),
                  {" "}<strong>Art. 5</strong> (non concorrenza 24 mesi),
                  {" "}<strong>Art. 8</strong> (clausola risolutiva espressa),
                  {" "}<strong>Art. 9</strong> (foro di Ascoli Piceno).
                </span>
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#0D1F3C]/15" data-testid="ws-cancel">Annulla</Button>
          <Button onClick={save} disabled={busy || (!isEdit && addendumState.needs && !acceptAddendum)} className="bg-[#1B4FD8] hover:bg-[#0D1F3C] disabled:opacity-50" data-testid="ws-save">
            {busy ? "Salvataggio..." : isEdit ? "Salva modifiche" : "Crea Workspace"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrashDialog({ open, onClose, workspaces, onChange }) {
  const now = Date.now();
  const daysLeft = (iso) => {
    if (!iso) return 0;
    const d = Math.ceil((new Date(iso).getTime() - now) / 86400000);
    return Math.max(0, d);
  };

  const restore = async (wsId) => {
    try {
      await axios.post(`${API}/workspaces/${wsId}/restore`, {}, { withCredentials: true });
      toast.success("Workspace ripristinato");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="ws-trash-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0D1F3C] flex items-center gap-2"><Trash size={18} weight="duotone" /> Cestino Workspace</DialogTitle>
          <DialogDescription className="text-xs text-[#0D1F3C]/60">
            I workspace eliminati sono conservati per 15 giorni. Dopo vengono eliminati definitivamente con tutti i dati.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2 max-h-[50vh] overflow-y-auto">
          {workspaces.length === 0 && (
            <p className="text-sm text-[#0D1F3C]/50 text-center py-6" data-testid="ws-trash-empty">Il cestino è vuoto.</p>
          )}
          {workspaces.map(w => (
            <div key={w.id} className="flex items-center gap-3 p-3 border border-[#0D1F3C]/10 rounded-lg" data-testid={`trash-item-${w.id}`}>
              <Buildings size={20} weight="duotone" className="text-[#0D1F3C]/40" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0D1F3C] truncate">{w.name}</p>
                <p className="text-xs text-[#0D1F3C]/50">
                  Eliminato il {new Date(w.deleted_at).toLocaleDateString('it-IT')} · {daysLeft(w.hard_delete_at)}gg residui
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={() => restore(w.id)} className="gap-1 border-[#1B4FD8] text-[#1B4FD8]" data-testid={`trash-restore-${w.id}`}>
                <ArrowCounterClockwise size={14} weight="duotone" /> Ripristina
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#0D1F3C]/15" data-testid="ws-trash-close">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
